import type {
  Media,
  Variant,
} from '../../../lib/flotiq-client/types/api-response';

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import { CaretDownIcon, WarningIcon } from '../../../images/shapes';

import Button from '../../Button/Button';
import Tooltip from '../../Tooltip/Tooltip';
import VariantCard from '../../VariantCard/VariantCard';

export type VariantsGridCreateFn = (media: Media) => Promise<void>;
export type VariantsGridDeleteFn = (media: Media, idx: number) => Promise<void>;

interface VariantsGridProps {
  /**
   * Relation object
   */
  relation: Media;
  /**
   * On variant create callback
   */
  onVariantCreate?: VariantsGridCreateFn;
  /**
   * On variant delete callback
   */
  onVariantDelete?: VariantsGridDeleteFn;
  /**
   * Relations card test id
   */
  testId?: string;
}

const VariantsGrid = ({
  relation,
  onVariantCreate,
  onVariantDelete,
  testId = '',
}: VariantsGridProps) => {
  const { t } = useTranslation();
  const [variantsOpen, setVariantsOpen] = useState(false);

  const handleVariantsOpen = useCallback(
    () => setVariantsOpen(!variantsOpen),
    [variantsOpen],
  );

  return (
    <div className="rounded-lg rounded-t-none p-4 space-y-4 border-t border-slate-400 dark:border-slate-700">
      <div className="flex flex-wrap justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            buttonColor="borderless"
            type="button"
            onClick={handleVariantsOpen}
            additionalClasses="font-normal text-slate-400 "
            additionalIconClasses="min-w-2.5 w-2.5 ml-1"
            iconImage={
              <CaretDownIcon
                className={twMerge('w-2.5', variantsOpen ? '' : 'rotate-180')}
              />
            }
            {...getTestProps(testId, 'existing-variants', 'testId')}
          >
            {t('MediaEdit.ExistingVariants', {
              length: relation?.variants?.length || 0,
            })}
          </Button>

          <Tooltip
            tooltip={t('ContentForm.VariantTooltip')}
            tooltipPlacement="topCenter"
            phoneTooltipPlacement="topCenter"
          >
            <div className="w-fit">
              <WarningIcon className="w-3.5 text-slate-400" />
            </div>
          </Tooltip>
        </div>
        <Button
          buttonColor="borderless"
          type="button"
          onClick={() => onVariantCreate?.(relation)}
          additionalClasses="font-normal"
          {...getTestProps(testId, 'create-variant', 'testId')}
        >
          {t('MediaEdit.AddVariant')}
        </Button>
      </div>
      {variantsOpen && relation?.variants && relation.variants?.length > 0 && (
        <div className="flex gap-4 pb-2 overflow-x-overlay scrollbar-sm">
          {relation.variants.map((variant: Variant, idx: number) => (
            <div key={variant.name}>
              <VariantCard
                variant={variant}
                media={relation}
                onDelete={
                  onVariantDelete
                    ? () => onVariantDelete(relation, idx)
                    : undefined
                }
                deleteTooltip={t('ContentForm.VariantDeleteTooltip')}
                additionalContainerClasses={'w-56'}
                {...getTestProps(testId, variant.name, 'testId')}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VariantsGrid;
